import { useCallback, useEffect, useRef, useState } from 'react';
import { Dicom } from '@/models/dicom';
import { useStudyReadiness } from '../studyReadiness';

/** 'thumbnails' readiness gate (AP-778 §6): the reveal waits until every
 *  rendered thumbnail has SETTLED (loaded or errored). Thumbnail-less dicoms
 *  render a static fallback and are excluded from the count. Returns the
 *  per-item settle callback DicomItem reports through. */
export function useThumbnailsGate(
  studyUuid: string | undefined,
  renderedDicoms: Dicom[]
): (uuid: string) => void {
  const { reportGate } = useStudyReadiness();
  const settledRef = useRef<Set<string>>(new Set());
  const [settledCount, setSettledCount] = useState(0);

  const handleThumbnailSettled = useCallback((uuid: string) => {
    if (settledRef.current.has(uuid)) return;
    settledRef.current.add(uuid);
    setSettledCount(settledRef.current.size);
  }, []);

  const expected = renderedDicoms.filter((d) => d.thumbnail);
  const ready =
    expected.filter((d) => settledRef.current.has(d.uuid)).length ===
    expected.length;

  useEffect(() => {
    if (!studyUuid) return;
    reportGate(studyUuid, 'thumbnails', ready);
    // settledCount re-runs the check as thumbnails land.
  }, [studyUuid, ready, settledCount, reportGate]);

  return handleThumbnailSettled;
}
